import * as Sentry from '@sentry/browser';
import { fetchRequest } from 'Utils/apiFetchRequest';
import SearchAutocomplete from './SearchAutocomplete';

const MOBILE_BREAKPOINT = 992 - 1;

function setupNavigation() {
    const dom = {
        headerContainer: document.querySelector('.js_mainHeader'),
        browseContainer: document.querySelector('.js_navBrowse'),
        profileContainer: document.querySelector('.js_navProfile'),
        specialtyContainer: document.querySelector('.js_navSpecialty'),
        termsContainer: document.querySelector('.js_navTerms'),
        toggleButton: document.querySelector('.js_navToggle'),
        toggleIcon: document.querySelector('.js_navToggle .icon'),
        toggleSearch: document.querySelector('.js_navSearchToggle'),
    };

    const state = {
        visible: false,
    };

    const hideNavigation = () => {
        dom.browseContainer.classList.add('hidden');
        dom.profileContainer.classList.add('hidden');
        dom.specialtyContainer.classList.add('hidden');
        dom.termsContainer.classList.add('hidden');

        dom.toggleIcon.classList.remove('icon-x');
        dom.toggleIcon.classList.add('icon-menu');

        dom.toggleSearch.classList.remove('hidden');
        dom.headerContainer.classList.remove('mobile-open');
        document.body.style.overflow = '';

        state.visible = false;
    };

    const showNavigation = (isMobile) => {
        dom.browseContainer.classList.remove('hidden');
        dom.profileContainer.classList.remove('hidden');
        dom.specialtyContainer.classList.remove('hidden');
        dom.termsContainer.classList.remove('hidden');

        dom.toggleIcon.classList.remove('icon-menu');
        dom.toggleIcon.classList.add('icon-x');
        dom.toggleSearch.classList.add('hidden');

        if (isMobile) {
            dom.headerContainer.classList.add('mobile-open');
            document.body.style.overflow = 'hidden';
        }

        state.visible = true;
    };

    // User is not logged in
    if (!dom.toggleButton) {
        return;
    }

    dom.toggleButton.addEventListener('click', (ev) => {
        ev.preventDefault();

        if (state.visible) {
            hideNavigation();
        } else {
            showNavigation(true);
        }
    });

    window.addEventListener('resize', () => {
        if (window.innerWidth > MOBILE_BREAKPOINT) {
            showNavigation(false);
        } else {
            hideNavigation();
        }
    });

    if (window.innerWidth > MOBILE_BREAKPOINT) {
        showNavigation(false);
    }
}

function setupSearch() {
    const dom = {
        headerContainer: document.querySelector('.js_mainHeader'),
        container: document.querySelector('.js_navSearch'),
        input: document.querySelector('.js_navSearch-input'),
        clear: document.querySelector('.js_navSearch-clear'),
        toggleIcon: document.querySelector('.js_navSearchToggle .icon'),
        toggleNav: document.querySelector('.js_navToggle'),
        toggleSearch: document.querySelector('.js_navSearchToggle'),
    };

    const state = {
        innerWidth: window.innerWidth,
        visible: false,
    };

    const hideSearch = () => {
        dom.container.classList.add('hidden');
        dom.toggleNav.classList.remove('hidden');
        dom.toggleIcon.classList.add('icon-search');
        dom.toggleIcon.classList.remove('icon-x');
        dom.toggleSearch.classList.remove('opened');
        dom.headerContainer.classList.remove('mobile-open');
        document.body.style.overflow = '';

        state.visible = false;
    };

    const showSearch = (isMobile) => {
        dom.container.classList.remove('hidden');
        dom.toggleNav.classList.add('hidden');
        dom.toggleIcon.classList.remove('icon-search');
        dom.toggleIcon.classList.add('icon-x');
        dom.toggleSearch.classList.add('opened');

        if (isMobile) {
            dom.input.focus();
            dom.headerContainer.classList.add('mobile-open');
            document.body.style.overflow = 'hidden';
        }

        state.visible = true;
    };

    // User is not logged in
    if (!dom.toggleSearch) {
        return;
    }

    dom.toggleSearch.addEventListener('click', (ev) => {
        ev.preventDefault();

        if (state.visible) {
            hideSearch();
        } else {
            showSearch(true);
        }
    });

    window.addEventListener('resize', () => {
        // Don't act if resolution stayed within the same device type
        if (
            (state.innerWidth < MOBILE_BREAKPOINT && window.innerWidth < MOBILE_BREAKPOINT) ||
            (state.innerWidth > MOBILE_BREAKPOINT && window.innerWidth > MOBILE_BREAKPOINT)
        ) {
            return;
        }

        state.innerWidth = window.innerWidth;

        if (window.innerWidth > MOBILE_BREAKPOINT) {
            showSearch(false);
        } else {
            hideSearch();
        }
    });

    if (window.innerWidth > MOBILE_BREAKPOINT) {
        showSearch(false);
    }

    if (dom.container && dom.input && dom.clear) {
        const autocomplete = new SearchAutocomplete(dom.container, dom.input, dom.clear);
        autocomplete.initialize();
    }
}

/**
 * In case that there are highlighted disease pages on the backend, show red circle with number of
 * unseen videos next to every disease term in the secondary navigation.
 *
 * Keep in mind that disease term can be instead, i.e. inside a dropdown, in which case a parent
 * will also get red circle.
 *
 * Highly coupled with "navigation.html" template.
 *
 * @see STEAM-296
 */
function showDiseasePageBadge() {
    const createHighlightElement = (count) => {
        const highlightElement = document.createElement('span');

        highlightElement.innerText = count.toString();
        highlightElement.classList.add('highlight');

        return highlightElement;
    };

    const addRedCircleToElement = (el, count) => {
        el.appendChild(createHighlightElement(count));
    };

    const addRedCricleToParent = (el, count) => {
        const existing = el.querySelector('.highlight');

        if (existing) {
            existing.innerText = parseInt(existing.innerText, 10) + count;
        } else {
            const iconElement = el.querySelector('.icon');

            el.insertBefore(createHighlightElement(count), iconElement);
        }
    };

    fetchRequest('/api/highlighted-disease-pages/')
        .then((data) => {
            if (!data.results?.length) {
                return;
            }

            for (const term of data.results) {
                const target = document.querySelector(`#navbarNavigation [href="${term.url}"]`);

                if (!target) {
                    continue;
                }

                const count = term.unseen_key_practice_update_videos.length;

                addRedCircleToElement(target, count);

                if (target.parentElement.classList.contains('dropdown-menu')) {
                    const targetParent = target.parentElement.previousElementSibling;

                    if (targetParent.getAttribute('role') === 'button') {
                        addRedCricleToParent(targetParent, count);
                    }
                }
            }
        })
        .catch((error) => {
            Sentry.captureException(error);
        });
}

document.addEventListener('DOMContentLoaded', () => {
    setupNavigation();
    setupSearch();
    showDiseasePageBadge();
});
